import api from './client'

export type InvoiceStatus = 'draft' | 'created' | 'sent' | 'accepted' | 'declined' | 'withdrawn' | 'paid'
export type DiscountType = 'fixed' | 'percent'

export interface InvoiceItemInput {
  serviceId?: string | null
  name?: string
  unitPrice?: number
  quantity: number
  discountType?: DiscountType | null
  discountValue?: number | null
  categoryId?: string | null
}

export interface InvoicePayload {
  leadId?: string
  dealId?: string
  patientName: string
  patientPhone?: string
  patientEmail?: string
  discountType?: DiscountType | null
  discountValue?: number | null
  notes?: string
  validUntil?: string
  language?: 'en' | 'it'
  includedServiceIds?: string[]
  items: InvoiceItemInput[]
}

export type InvoiceUpdatePayload = Pick<InvoicePayload, 'discountType' | 'discountValue' | 'notes' | 'validUntil' | 'items' | 'language' | 'includedServiceIds'> & { internalNotes?: string }

export interface InvoiceItem {
  id: string
  serviceId: string | null
  name: string
  originalPrice: string
  unitPrice: string
  quantity: number
  discountType: DiscountType | null
  discountValue: string | null
  lineTotal: string
  sortOrder: number
}

/**
 * The clinic visit booked when the proforma was accepted. Present on list rows
 * only, and null for every proforma that has not been accepted.
 */
export interface InvoiceAppointment {
  id: string
  startAt: string | null
  endAt: string | null
  note: string | null
  leadName: string | null
  leadSource: string | null
  createdAt: string
  createdByUser: { id: string; firstName: string; lastName: string } | null
}

/**
 * A visit still ahead that withdrawing an accepted proforma would cancel.
 * Present on the detail endpoint only.
 */
export interface UpcomingAppointment {
  id: string
  isAllDay: boolean
  allDayDate: string | null
  startAt: string | null
  endAt: string | null
}

/** The visit booked alongside an `accepted` transition. */
export interface AcceptAppointmentInput {
  startAt: string
  endAt?: string | null
  note?: string
}

export interface Invoice {
  id: string
  number: string
  status: InvoiceStatus
  leadId: string | null
  dealId: string | null
  patientName: string
  patientPhone: string | null
  patientEmail: string | null
  currency: string
  discountType: DiscountType | null
  discountValue: string | null
  subtotal: string
  total: string
  notes: string | null
  internalNotes: string | null
  validUntil: string | null
  language: 'en' | 'it'
  includedServices: { id: string; textEn: string | null; textIt: string | null }[] | null
  sharedIn: string | null
  createdById: string
  createdByUser?: { id: string; firstName: string; lastName: string }
  createdAt: string
  updatedAt: string
  items?: InvoiceItem[]
  appointment?: InvoiceAppointment | null
  upcomingAppointments?: UpcomingAppointment[]
}

export interface InvoiceListMeta { page: number; limit: number; total: number; totalPages: number }

const base = (cid: string) => `/companies/${cid}/invoices`

export const invoicesApi = {
  list: (cid: string, params: {
    status?: InvoiceStatus; leadId?: string; dealId?: string; search?: string;
    page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc';
  } = {}) =>
    api.get<{ data: Invoice[]; meta: InvoiceListMeta }>(base(cid), { params }).then(r => r.data),

  get: (cid: string, id: string) =>
    api.get<Invoice>(`${base(cid)}/${id}`).then(r => r.data),

  create: (cid: string, dto: InvoicePayload) =>
    api.post<Invoice>(base(cid), dto).then(r => r.data),

  update: (cid: string, id: string, dto: InvoiceUpdatePayload) =>
    api.patch<Invoice>(`${base(cid)}/${id}`, dto).then(r => r.data),

  updateStatus: (cid: string, id: string, dto: { status: InvoiceStatus; sharedIn?: string; appointment?: AcceptAppointmentInput }) =>
    api.patch<Invoice>(`${base(cid)}/${id}/status`, dto).then(r => r.data),

  setLanguage: (cid: string, id: string, language: 'en' | 'it') =>
    api.patch<Invoice>(`${base(cid)}/${id}/language`, { language }).then(r => r.data),

  /** New draft with the same content — for proformas that can no longer be edited. */
  copy: (cid: string, id: string) =>
    api.post<Invoice>(`${base(cid)}/${id}/copy`).then(r => r.data),

  remove: (cid: string, id: string) => api.delete(`${base(cid)}/${id}`),

  preview: (cid: string, dto: InvoicePayload & { invoiceId?: string }) =>
    api.post<Blob>(`${base(cid)}/preview`, dto, { responseType: 'blob' }).then(r => r.data),

  pdfBlob: (cid: string, id: string) =>
    api.get<Blob>(`${base(cid)}/${id}/pdf`, { responseType: 'blob' }).then(r => r.data),
}
